import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  CircularProgress,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useCreateTrainingTopic } from '../../../api/hooks/useTrainingTopics';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { getNextSequence } from '../utils/sequenceHelpers';

interface CreateTopicModalProps {
  open: boolean;
  onClose: () => void;
  trainingId: string;
  moduleId: string;
  existingTopics?: any[];
}

const CreateTopicModal: React.FC<CreateTopicModalProps> = ({
  open,
  onClose,
  trainingId,
  moduleId,
  existingTopics = [],
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [progressContribution, setProgressContribution] = useState(100);
  
  const { selectedOrganization } = useOrganization();
  const { enqueueSnackbar } = useSnackbar();
  const createMutation = useCreateTrainingTopic(
    selectedOrganization?.id || '',
    trainingId,
    moduleId
  );

  const handleSubmit = async () => {
    if (!name.trim()) {
      enqueueSnackbar('El nombre es requerido', { variant: 'error' });
      return;
    }

    if (progressContribution < 0 || progressContribution > 100) {
      enqueueSnackbar('La contribución al progreso debe estar entre 0 y 100', {
        variant: 'error',
      });
      return;
    }

    try {
      const nextSequence = getNextSequence(existingTopics);
      await createMutation.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
        sequence: nextSequence,
        progressContribution,
        moduleId,
        isActive: true,
      });

      enqueueSnackbar('Tema creado exitosamente', { variant: 'success' });
      handleClose();
    } catch (error) {
      enqueueSnackbar('Error al crear el tema', { variant: 'error' });
      console.error('Error creating topic:', error);
    }
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    setProgressContribution(100);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Crear Nuevo Tema</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <TextField
            label="Nombre"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            autoFocus
          />
          <TextField
            label="Descripción"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={3}
            fullWidth
          />
          <TextField
            label="Contribución al Progreso (%)"
            type="number"
            value={progressContribution}
            onChange={(e) =>
              setProgressContribution(parseInt(e.target.value) || 0)
            }
            required
            fullWidth
            helperText="La suma de todos los temas del módulo debe ser 100%"
            InputProps={{ 
              inputProps: { 
                min: 0, 
                max: 100,
                onWheel: (e) => e.currentTarget.blur()
              } 
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={createMutation.isPending}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={createMutation.isPending}
          startIcon={createMutation.isPending && <CircularProgress size={20} />}
        >
          Crear
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTopicModal;



